import {
  Alert,
  Box,
  Button,
  Card,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Switch,
  Textarea,
  Typography,
} from "@mui/joy";
import type {
  PresentationOptions,
  ProposalTheme,
} from "../../types/proposalPresentation";

import {
  presentationRowGroups as groups,
  presentationDraftIssues,
} from "../../lib/proposalPresentationDraft";

export default function PresentationSettingsEditor({
  value,
  onChange,
  brand,
  inspectionPageEnabled,
}: {
  value: PresentationOptions;
  onChange: (value: PresentationOptions) => void;
  brand?: ProposalTheme;
  inspectionPageEnabled?: boolean;
}) {
  const issues = presentationDraftIssues(value);
  const update = (key: keyof PresentationOptions, next: unknown) =>
    onChange({ ...value, [key]: next });
  const payments = (value.milestones || []).filter(
    (row) => row.percent !== undefined,
  );
  const paymentTotal = payments.reduce(
    (sum, row) => sum + Math.round(Number(row.percent) * 100),
    0,
  );
  return (
    <Stack spacing={2.5}>
      <Typography level="h4">Proposal presentation</Typography>
      <Typography>
        Brand colors, logo variants, and heading/body typefaces come from your
        organization. These settings apply to the PDF and customer experience.
      </Typography>
      <FormControl orientation="horizontal">
        <FormLabel>Use the branded proposal experience</FormLabel>
        <Switch
          checked={value.enabled !== false}
          onChange={(event) => update("enabled", event.target.checked)}
        />
      </FormControl>
      {brand && (
        <Card variant="outlined">
          <Typography level="title-md">Organization palette</Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {brand.colors.filter(Boolean).map((color, i) => (
              <Box
                key={i}
                title={color!}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "sm",
                  bgcolor: color!,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              />
            ))}
          </Stack>
          <Typography level="body-sm">
            Headings: {brand.heading_font} · Body: {brand.body_font}
          </Typography>
          <Typography level="body-sm">
            Light and dark surfaces, photo overlays, and gradients are derived
            from the primary color. Logos use the variant suited to the
            background.
          </Typography>
        </Card>
      )}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel>Default theme</FormLabel>
          <Select
            value={value.theme || "light"}
            onChange={(_, next) => next && update("theme", next)}
          >
            <Option value="light">Light</Option>
            <Option value="dark">Dark</Option>
          </Select>
        </FormControl>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel>Inspection photos</FormLabel>
          <Select
            value={value.photo_mode || "all"}
            onChange={(_, next) => next && update("photo_mode", next)}
          >
            <Option value="all">All opportunity photos</Option>
            <Option value="selected">Selected photos only</Option>
          </Select>
        </FormControl>
      </Stack>
      <Card>
        <Typography level="title-md">Cover photo focal point</Typography>
        <Typography level="body-sm">
          0% is left/top; 100% is right/bottom. Inspection photos retain the
          full image.
        </Typography>
        <Stack direction="row" spacing={2}>
          {["Horizontal", "Vertical"].map((label, i) => (
            <FormControl key={label}>
              <FormLabel>{label} %</FormLabel>
              <Input
                type="number"
                value={(value.cover_position || [50, 50])[i]}
                slotProps={{ input: { min: 0, max: 100 } }}
                onChange={(event) => {
                  const position = [...(value.cover_position || [50, 50])] as [
                    number,
                    number,
                  ];
                  position[i] = Math.min(
                    100,
                    Math.max(0, Number(event.target.value)),
                  );
                  update("cover_position", position);
                }}
              />
            </FormControl>
          ))}
        </Stack>
      </Card>
      {groups.map((group) => {
        const rows = (value[group.key] || []) as unknown as Record<
          string,
          string
        >[];
        const setRows = (next: Record<string, string>[]) =>
          update(group.key, next);
        return (
          <Card key={group.key}>
            <Typography level="title-lg">{group.title}</Typography>
            {group.key === "findings" &&
              (inspectionPageEnabled === false ? (
                <Alert color="warning">
                  The Inspection page is turned off. Turn on Inspection in the
                  Pages panel to include these findings in the customer preview
                  and PDF.
                </Alert>
              ) : (
                <Typography level="body-sm">
                  Findings appear on the Inspection page in the customer preview
                  and PDF, even when there are no inspection photos.
                </Typography>
              ))}
            {group.key === "credentials" && (
              <Typography level="body-sm">
                Include only credentials and claims verified for this
                organization.
              </Typography>
            )}
            {group.key === "milestones" && (
              <Typography level="body-sm">
                Leave payment % blank for a non-payment milestone. Entered
                percentages must total 100%; final amounts follow the selected
                package and upgrades.
              </Typography>
            )}
            {rows.map((row, index) => (
              <Card key={index} variant="soft">
                <Stack direction="row" justifyContent="space-between">
                  <Typography level="title-sm">{index + 1}</Typography>
                  <Button
                    size="sm"
                    variant="plain"
                    color="danger"
                    onClick={() => setRows(rows.filter((_, i) => i !== index))}
                  >
                    Remove
                  </Button>
                </Stack>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                    },
                    gap: 1.5,
                  }}
                >
                  {group.fields.map(({ key: field, label, required }) => (
                    <FormControl
                      key={field}
                      required={required}
                      error={!!issues[`${group.key}.${index}.${field}`]}
                    >
                      <FormLabel>{label}</FormLabel>
                      {["description", "impact"].includes(field) ? (
                        <Textarea
                          minRows={2}
                          value={row[field] || ""}
                          onChange={(event) =>
                            setRows(
                              rows.map((item, i) =>
                                i === index
                                  ? { ...item, [field]: event.target.value }
                                  : item,
                              ),
                            )
                          }
                        />
                      ) : (
                        <Input
                          type={field === "percent" ? "number" : "text"}
                          value={row[field] || ""}
                          slotProps={
                            field === "percent"
                              ? { input: { min: 0, max: 100, step: 0.01 } }
                              : undefined
                          }
                          onChange={(event) =>
                            setRows(
                              rows.map((item, i) => {
                                if (i !== index) return item;
                                const next = {
                                  ...item,
                                  [field]: event.target.value,
                                };
                                if (field === "percent" && !event.target.value)
                                  delete next.percent;
                                return next;
                              }),
                            )
                          }
                        />
                      )}
                      {issues[`${group.key}.${index}.${field}`] && (
                        <FormHelperText>
                          {issues[`${group.key}.${index}.${field}`]}
                        </FormHelperText>
                      )}
                    </FormControl>
                  ))}
                </Box>
              </Card>
            ))}
            {group.key === "milestones" && payments.length > 0 && (
              <Alert color={paymentTotal === 10000 ? "success" : "warning"}>
                Payment allocation: {paymentTotal / 100}%
                {paymentTotal !== 10000 && " · Adjust to 100% before saving."}
              </Alert>
            )}
            <Button
              variant="outlined"
              size="sm"
              disabled={rows.length >= 100}
              onClick={() =>
                setRows([
                  ...rows,
                  Object.fromEntries(
                    group.fields
                      .filter(({ key }) => key !== "percent")
                      .map(({ key }) => [key, ""]),
                  ),
                ])
              }
            >
              Add{" "}
              {group.title === "Package comparison"
                ? "comparison row"
                : group.key === "credentials"
                  ? "credential"
                  : group.key === "milestones"
                    ? "milestone"
                    : "finding"}
            </Button>
          </Card>
        );
      })}
      <FormControl>
        <FormLabel>Next steps</FormLabel>
        <Textarea
          minRows={3}
          value={value.next_steps || ""}
          onChange={(event) => update("next_steps", event.target.value)}
        />
      </FormControl>
      <Typography level="body-sm">
        Complete required fields or remove unfinished rows before saving. Empty
        sections are omitted from the customer presentation.
      </Typography>
    </Stack>
  );
}
